import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { listPersonnel, deletePersonnel } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

// Keyed by the raw Vietnamese enum values the AI prompt always generates for
// these metadata fields (see backend/prompts/personnelPrompt.js) — unlike
// the main report narrative, these short classification fields are never
// generated in a separate English variant, so person.influenceLevel/
// overallRating/personnelGroup display as-is (Vietnamese) regardless of UI
// language. Not a frontend i18n gap — a backend data-layer limitation.
const INFLUENCE_COLOR = {
  'Nhà lãnh đạo ngành':  '#10b981',
  'Chuyên gia hàng đầu': '#3b82f6',
  'Chuyên gia':          '#8b5cf6',
  'Contributor':         '#f59e0b',
  'Chưa xác định':       '#475569',
};
const RATING_COLOR = {
  'Xuất sắc':         '#10b981',
  'Tốt':              '#3b82f6',
  'Trung bình':       '#f59e0b',
  'Yếu':              '#ef4444',
  'Chưa đủ dữ liệu':  '#475569',
};
const STATUS_KEY = {
  none:      { key: 'personnelPage.statusBasic',    color: '#475569' },
  pending:   { key: 'personnelPage.statusPending',   color: '#f59e0b' },
  running:   { key: 'personnelPage.statusRunning',   color: '#3b82f6' },
  completed: { key: 'personnelPage.statusCompleted', color: '#10b981' },
  failed:    { key: 'personnelPage.statusFailed',    color: '#ef4444' },
};

function Initials({ name }) {
  const parts = name.trim().split(/\s+/);
  const init = parts.length >= 2
    ? parts[0][0] + parts[parts.length - 1][0]
    : name.slice(0, 2);
  const colors = ['#10b981','#3b82f6','#8b5cf6','#f59e0b','#ec4899','#06b6d4','#ef4444','#14b8a6'];
  const bg = colors[(name.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{ ...s.avatar, background: bg }}>
      {init.toUpperCase()}
    </div>
  );
}

function ScoreDot({ score, loading }) {
  if (loading) return (
    <div style={s.scoreWrap}>
      <div style={{ ...s.scoreBar, width: '60%', background: 'var(--border-mid)', animation: 'pulse 1.2s ease infinite' }} />
      <span style={{ ...s.scoreNum, color: 'var(--text-dim)', fontSize: 9 }}>...</span>
    </div>
  );
  if (score == null || score === 0) return <span style={{ color: '#475569', fontSize: 11 }}>—</span>;
  const pct = (score / 10) * 100;
  const color = pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div style={s.scoreWrap}>
      <div style={{ ...s.scoreBar, width: `${pct}%`, background: color }} />
      <span style={{ ...s.scoreNum, color }}>{score.toFixed(1)}</span>
    </div>
  );
}

export default function PersonnelPage({ onViewDetail }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [persons, setPersons]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [sortBy, setSortBy]       = useState('createdAt');

  const load = useCallback(async () => {
    try {
      const { docs } = await listPersonnel();
      setPersons(docs);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);

  const handleDelete = async (slug, e) => {
    e.stopPropagation();
    if (!window.confirm(t('personnelPage.confirmDelete'))) return;
    await deletePersonnel(slug);
    await load();
  };

  const groups = [...new Set(persons.map(p => p.personnelGroup).filter(Boolean))];

  const filtered = persons
    .filter(p => {
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) &&
          !(p.project || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (filterGroup && p.personnelGroup !== filterGroup) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'influence') return (b.influenceScore || 0) - (a.influenceScore || 0);
      if (sortBy === 'credibility') return (b.credibilityScore || 0) - (a.credibilityScore || 0);
      if (sortBy === 'trust') return (b.trustScore || 0) - (a.trustScore || 0);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  const advancedCount = persons.filter(p => p.status === 'completed').length;

  const pageStyle    = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;
  const controlStyle = isMobile ? { ...s.controls, width: '100%' } : s.controls;
  const inputStyle   = isMobile ? { ...s.searchInput, width: '100%', flex: 1 } : s.searchInput;
  const gridStyle    = isMobile ? { ...s.grid, gridTemplateColumns: '1fr' } : s.grid;

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={isMobile ? { ...s.header, flexDirection: 'column', alignItems: 'flex-start' } : s.header}>
        <div>
          <h2 style={s.title}>👤 {t('personnelPage.title')}</h2>
          <p style={s.subtitle}>{t('personnelPage.subtitle', { total: persons.length, complete: advancedCount })}</p>
        </div>
        <div style={controlStyle}>
          <input
            style={inputStyle}
            placeholder={t('personnelPage.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={s.select} value={filterGroup} onChange={e => setFilterGroup(e.target.value)}>
            <option value="">{t('personnelPage.allGroups')}</option>
            {groups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select style={s.select} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="createdAt">{t('personnelPage.sortNewest')}</option>
            <option value="influence">{t('personnelPage.sortInfluence')}</option>
            <option value="credibility">{t('personnelPage.sortCredibility')}</option>
            <option value="trust">{t('personnelPage.sortTrust')}</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>{t('personnelPage.loading')}</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            {t('personnelPage.emptyTitle')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 400, textAlign: 'center' }}>
            {t('personnelPage.emptyDescPrefix')} <strong style={{ color: 'var(--text)' }}>{t('personnelPage.emptyDescHighlight')}</strong> {t('personnelPage.emptyDescSuffix')}
          </div>
        </div>
      ) : (
        <div style={gridStyle}>
          {filtered.map(person => {
            const st = STATUS_KEY[person.status] || STATUS_KEY.none;
            const infColor = INFLUENCE_COLOR[person.influenceLevel] || '#475569';
            const ratColor = RATING_COLOR[person.overallRating] || '#475569';
            return (
              <div key={person._id} style={s.card} onClick={() => onViewDetail(person.slug)}>
                {/* Status badge */}
                <div style={{ ...s.statusBadge, background: st.color + '20', color: st.color }}>
                  {person.status === 'running' && <span style={s.spinner} />}
                  {t(st.key)}
                </div>

                <div style={s.cardTop}>
                  <Initials name={person.name} />
                  <div style={s.cardMeta}>
                    <div style={s.cardName}>{person.name}</div>
                    <div style={s.cardRole}>{person.role || t('personnelPage.unknownRole')}</div>
                    <div style={s.cardProject}>
                      <span style={s.projectDot} />
                      {person.project || t('personnelPage.unknownProject')}
                    </div>
                  </div>
                </div>

                {/* Scores */}
                {(person.status === 'completed' || person.status === 'running' || person.status === 'pending') && (
                  <div style={s.scores}>
                    {['running', 'pending'].includes(person.status) && (
                      <div style={s.analyzingNote}>⏳ {t('personnelPage.analyzingScores')}</div>
                    )}
                    <div style={s.scoreRow}>
                      <span style={s.scoreLabel}>{t('personnelPage.scoreInfluence')}</span>
                      <ScoreDot score={person.influenceScore} loading={['running','pending'].includes(person.status)} />
                    </div>
                    <div style={s.scoreRow}>
                      <span style={s.scoreLabel}>{t('personnelPage.scoreCredibility')}</span>
                      <ScoreDot score={person.credibilityScore} loading={['running','pending'].includes(person.status)} />
                    </div>
                    <div style={s.scoreRow}>
                      <span style={s.scoreLabel}>{t('personnelPage.scoreTrust')}</span>
                      <ScoreDot score={person.trustScore} loading={['running','pending'].includes(person.status)} />
                    </div>
                  </div>
                )}
                {person.status === 'failed' && (
                  <div style={s.failedNote}>
                    ⚠️ {person.error || t('personnelPage.researchFailed')} — {t('personnelPage.viewToRetry')}
                  </div>
                )}
                {person.status === 'none' && (
                  <div style={s.noneNote}>{t('personnelPage.notResearchedYet')}</div>
                )}

                {/* Tags */}
                <div style={s.tags}>
                  {person.influenceLevel && (
                    <span style={{ ...s.tag, background: infColor + '18', color: infColor, borderColor: infColor + '40' }}>
                      {person.influenceLevel}
                    </span>
                  )}
                  {person.personnelGroup && (
                    <span style={{ ...s.tag, background: 'var(--surface2)', color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
                      {person.personnelGroup}
                    </span>
                  )}
                  {person.overallRating && person.overallRating !== 'Chưa đủ dữ liệu' && (
                    <span style={{ ...s.tag, background: ratColor + '18', color: ratColor, borderColor: ratColor + '40' }}>
                      ⭐ {person.overallRating}
                    </span>
                  )}
                  {(person.redFlags?.length > 0) && (
                    <span style={{ ...s.tag, background: '#ef444418', color: '#ef4444', borderColor: '#ef444440' }}>
                      ⚠️ {t('personnelPage.warningsCount', { count: person.redFlags.length })}
                    </span>
                  )}
                </div>

                <div style={s.cardFooter}>
                  <span style={s.viewBtn}>{t('personnelPage.viewProfile')} →</span>
                  <button style={s.delBtn} onClick={e => handleDelete(person.slug, e)}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    <style>{`
      @keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:0.9} }
      @keyframes spin { to{transform:rotate(360deg)} }
    `}</style>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  controls: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  searchInput: {
    background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 12px', fontSize: 13, width: 220, outline: 'none',
  },
  select: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-muted)', padding: '8px 12px', fontSize: 12, cursor: 'pointer',
  },
  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 20px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  card: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 20,
    cursor: 'pointer', transition: 'border-color 0.2s, transform 0.15s', position: 'relative',
    boxShadow: '0 1px 4px rgba(31,88,164,0.06)', minWidth: 0, overflow: 'hidden',
  },
  statusBadge: {
    position: 'absolute', top: 14, right: 14,
    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
    display: 'flex', alignItems: 'center', gap: 4,
  },
  spinner: {
    width: 7, height: 7, borderRadius: '50%',
    border: '1.5px solid transparent', borderTopColor: 'currentColor',
    display: 'inline-block', animation: 'spin 0.8s linear infinite',
  },
  cardTop: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 },
  avatar: {
    width: 52, height: 52, borderRadius: 14, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, fontWeight: 800, color: '#fff',
  },
  cardMeta: { flex: 1, minWidth: 0 },
  cardName: { fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 3, wordBreak: 'break-word', overflowWrap: 'break-word' },
  cardRole: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 5, wordBreak: 'break-word', overflowWrap: 'break-word' },
  cardProject: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--accent)', wordBreak: 'break-word', overflowWrap: 'break-word' },
  projectDot: { width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 },
  scores: { marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 },
  analyzingNote: { fontSize: 10, color: '#3b82f6', marginBottom: 4, fontStyle: 'italic' },
  failedNote: { fontSize: 11, color: '#ef4444', marginBottom: 14, lineHeight: 1.5, background: '#ef444410', borderRadius: 8, padding: '8px 10px', border: '1px solid #ef444430' },
  noneNote: { fontSize: 11, color: 'var(--text-dim)', marginBottom: 14, fontStyle: 'italic' },
  scoreRow: { display: 'flex', alignItems: 'center', gap: 10 },
  scoreLabel: { fontSize: 10, color: 'var(--text-dim)', width: 70, flexShrink: 0 },
  scoreWrap: { flex: 1, display: 'flex', alignItems: 'center', gap: 6 },
  scoreBar: { height: 4, borderRadius: 2, background: '#10b981', flex: 1, maxWidth: 120, transition: 'width 0.5s' },
  scoreNum: { fontSize: 11, fontWeight: 700, minWidth: 24 },
  tags: { display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 },
  tag: { fontSize: 10, padding: '3px 8px', borderRadius: 12, border: '1px solid', fontWeight: 600 },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  viewBtn: { fontSize: 12, color: 'var(--accent)', fontWeight: 600 },
  delBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)',
    fontSize: 14, padding: '2px 6px', borderRadius: 4,
  },
};
